import React, { useEffect, useState } from "react";
import {
  Table,
  Avatar,
  Tag,
  Button,
  Select,
  Tooltip,
  Spin,
  Card,
  App,
} from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { apiGetPatients } from "../../api/patients";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "./patients.css";

dayjs.extend(relativeTime);

const { Option } = Select;

const Patients = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("All");
  const [riskFilter, setRiskFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [patientsData, setPatientsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [meta, setMeta] = useState({
    currentPage: 1,
    perPage: 10,
    totalPages: 1,
    total: 0,
  });

  const fetchPatientsData = async () => {
    try {
      const filterMap = {
        Low: 1,
        Medium: 2,
        High: 3,
      };

      const statusMap = {
        Active: true,
        Inactive: false,
      };

      const riskLevel =
        riskFilter !== "All" ? filterMap[riskFilter] : undefined;
      const status =
        statusFilter !== "All" ? statusMap[statusFilter] : undefined;

      setLoading(true);
      const res = await apiGetPatients({
        page: currentPage,
        perPage: meta.perPage,
        risk_level: riskLevel,
        active: status,
      });
      setPatientsData(res.data);
      setMeta(res.meta);
    } catch (error) {
      console.error("Patients fetch error:", error);
      message.error("Không thể tải dữ liệu bệnh nhân");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatientsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskFilter, statusFilter, currentPage]);

  const getRiskLevel = (risk) => {
    switch (risk) {
      case 1:
        return "Low";
      case 2:
        return "Medium";
      case 3:
        return "High";
      default:
        return "-";
    }
  };

  const getRiskLevelColor = (risk) => {
    switch (risk) {
      case 1:
        return "blue";
      case 3:
        return "gold";
      case 2:
        return "red";
      default:
        return "-";
    }
  };

  const columns = [
    {
      title: "Bệnh nhân",
      dataIndex: "fullName",
      key: "fullName",
      render: (text, record) => (
        <div className="flex items-center gap-3">
          <Avatar
            src={record?.profileImage}
            size={40}
            style={{ backgroundColor: record.avatarColor || "#9333ea" }}
          >
            {text?.charAt(0)}
          </Avatar>
          <div>
            <div className="font-medium text-gray-900">{text}</div>
            <div className="text-sm text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Mã bệnh nhân",
      dataIndex: "patientDisplayId",
      key: "patientDisplayId",
      render: (text) => <span className="text-gray-700">{text}</span>,
    },
    {
      title: "Hoạt động cuối cùng",
      dataIndex: "lastActivity",
      key: "lastActivity",
      render: (text) => {
        const timestamp = Number(text);
        const time =
          timestamp < 10000000000
            ? dayjs.unix(timestamp) // seconds → convert correctly
            : dayjs(timestamp); // already ms

        return <span className="text-gray-700">{time.fromNow()}</span>;
      },
    },
    {
      title: "Trạng thái",
      dataIndex: "isActive",
      key: "isActive",
      render: (status) => (
        <Tag className="risk-tag" color={status ? "green" : ""}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Mức độ rủi ro",
      dataIndex: "riskLevel",
      key: "riskLevel",
      render: (risk) => {
        const level = getRiskLevel(risk);
        return (
          <Tag
            color={getRiskLevelColor(risk)}
            className={`risk-tag risk-${level.toLowerCase()}`}
          >
            {level}
          </Tag>
        );
      },
    },
    {
      title: "Hành động",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              className="action-btn"
              onClick={() => navigate(`/patients/${record?.id}`)}
            />
          </Tooltip>
          <Tooltip title="Book Appointment">
            <Button
              type="text"
              icon={
                <img
                  src="/icon/appointments_icon.svg"
                  alt="Calendar"
                  className="w-4 h-4"
                />
              }
              className="action-btn"
              onClick={() =>
                record?.upcomingAppointment
                  ? navigate(`/appointments/${record?.upcomingAppointment}`)
                  : navigate("/appointments/create", {
                      state: {
                        patient: {
                          id: record.id,
                          patientId: record.id,
                          name: record.fullName,
                          profileImage: record.profileImage,
                        },
                      },
                    })
              }
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="patients-page">
      {/* Header with Filters on Same Row */}
      <div className="patients-header-row">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Patients</h1>
          <p className="text-sm text-gray-500 mt-1">
            Tổng quan về bệnh nhân và theo dõi tiến trình điều trị
          </p>
        </div>

        {/* Filters Card */}
        <Card className="shadow-m mb-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-16">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  Trạng thái:
                </span>
                <Select
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: 140 }}
                >
                  <Option value="All">Tất cả</Option>
                  <Option value="Active">Hoạt động</Option>
                  <Option value="Inactive">Không hoạt động</Option>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  Mức độ rủi ro:
                </span>
                <Select
                  value={riskFilter}
                  onChange={setRiskFilter}
                  style={{ width: 140 }}
                >
                  <Option value="All">Tất cả</Option>
                  <Option value="High">Cao</Option>
                  <Option value="Medium">Trung bình</Option>
                  <Option value="Low">Thấp</Option>
                </Select>
              </div>
            </div>

            <Button
              variant="link"
              color="primary"
              className="text-purple-600 font-medium"
              onClick={() => {
                setStatusFilter("All");
                setRiskFilter("All");
              }}
            >
              Xóa bộ lọc
            </Button>
          </div>
        </Card>
      </div>

      {/* Table */}
      <div className="patients-table">
        <Table
          columns={columns}
          dataSource={patientsData}
          rowKey="id"
          pagination={{
            current: currentPage,
            total: meta.total,
            pageSize: meta.perPage,
            showSizeChanger: false,
            onChange: (page) => setCurrentPage(page),
            position: ["bottomRight"],
            showTotal: (total, range) => (
              <div className="pagination-info">
                Đang hiển thị {range[0]}-{range[1]} của {total} bệnh nhân
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default Patients;
